/**
 * #41 (2026-06-12) — Reusable per-column filter primitives.
 *
 * Pedro's standing requirement: "build it so we can apply it to other
 * lists as well in the future." This file is deliberately GENERIC — no
 * entity-specific imports or logic. Each control is a compact (~28px)
 * input designed to sit inside a second <Table.Tr> in <Table.Thead>,
 * directly under the column-label row (Option B placement, Pedro's
 * choice 2026-06-12).
 *
 * §3h canonical reference implementation: AgreementInteractionsTab.
 * When porting to another list page, copy its shape:
 *
 *   <Table.Thead>
 *     <Table.Tr>{column labels}</Table.Tr>
 *     <Table.Tr>{one ColumnXxxFilter (or empty <Table.Th/>) per column}</Table.Tr>
 *   </Table.Thead>
 *
 * Contract (C-131): every control is fully controlled — its value lives
 * in the page's filter state, and that state MUST be passed through to
 * the query (a control without a consumer is a §3j lie). Pages must also
 * gate EVERY render branch on the filtered query and distinguish
 * "no records at all" from "no records match the current filters"
 * (pair with hasActiveFilters below).
 */
import {
  CloseButton, Combobox, Group, Input, InputBase, Select, Stack, TextInput,
  Tooltip, useCombobox,
} from '@mantine/core';
import { IconCheck, IconFilter } from '@tabler/icons-react';
import * as React from 'react';

const COMPACT = {
  size: 'xs' as const,
};

export interface ColumnSelectOption {
  value: string;
  label: string;
}

/** Flat or grouped option list (Mantine Select shape). */
export type ColumnSelectData =
  | ColumnSelectOption[]
  | { group: string; items: ColumnSelectOption[] }[];

/** P41 blank filter (Pedro 2026-10-15): typing "-" in any column text filter means "this
 *  field is blank". The server applies it (app/search.text_match); client-side tables use
 *  `textMatches` so the convention reads the same on every list (§3h). */
export const BLANK_QUERY = '-';
const foldText = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Client-side text-filter test: "-" → the value is blank; otherwise an accent- and
 *  case-insensitive substring match. An empty filter matches everything. */
export function textMatches(value: string | null | undefined, q: string): boolean {
  if (!q) return true;
  if (q.trim() === BLANK_QUERY) return !value || value.trim() === '';
  return foldText(value ?? '').includes(foldText(q));
}

/** Debounced free-text filter. Emits onChange after `debounceMs` of
 *  keyboard silence (and immediately on clear). Typing "-" finds blank values (P41). */
export function ColumnTextFilter({
  value, onChange, placeholder, debounceMs = 350, ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  debounceMs?: number;
  ariaLabel?: string;
}) {
  const [draft, setDraft] = React.useState(value);
  React.useEffect(() => setDraft(value), [value]);
  React.useEffect(() => {
    if (draft === value) return;
    const t = setTimeout(() => onChange(draft), debounceMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);
  return (
    <Tooltip label="Type - to find rows where this is blank" withArrow openDelay={600}>
      <TextInput {...COMPACT}
        aria-label={ariaLabel ?? placeholder ?? 'Filter'}
        placeholder={placeholder ?? 'Search…'}
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        rightSection={draft
          ? <CloseButton size="xs" aria-label="Clear"
              onClick={() => { setDraft(''); onChange(''); }} />
          : undefined}
      />
    </Tooltip>
  );
}

/** Single-choice dropdown filter. `null` = no filter ("All"). */
export function ColumnSelectFilter({
  value, onChange, options, allLabel = 'All', ariaLabel,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  options: ColumnSelectData;
  allLabel?: string;
  ariaLabel?: string;
}) {
  return (
    <Select {...COMPACT}
      aria-label={ariaLabel ?? allLabel}
      placeholder={allLabel}
      data={options}
      value={value}
      onChange={onChange}
      clearable searchable
      comboboxProps={{ withinPortal: true }}
    />
  );
}

function flattenOptions(options: ColumnSelectData): ColumnSelectOption[] {
  return options.flatMap((o) => ('items' in o ? o.items : [o]));
}

/** Multi-choice dropdown filter for CLOSED option sets (Pedro's decision
 *  2026-06-12, C-132 amendment). Empty array = no filter. Backend side
 *  takes a repeated list param. Note: on a nullable column, selecting
 *  EVERY option means "any value set" — blanks stay excluded. Dynamic
 *  sets (attendees, authors) keep the single ColumnSelectFilter.
 *
 *  Design redone 2026-06-12 on Pedro's feedback ("the filter box looks
 *  crowded"): no in-input pills. The dropdown is a tick-in-succession
 *  checkbox list (the attendee-picker pattern from InteractionFormModal)
 *  that stays OPEN across clicks; the input shows the single selected
 *  label, or "Several (n)" when more than one, with a clear ×. */
export function ColumnMultiSelectFilter({
  values, onChange, options, allLabel = 'All', ariaLabel,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  options: ColumnSelectData;
  allLabel?: string;
  ariaLabel?: string;
}) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });
  const flat = flattenOptions(options);
  const toggle = (val: string) => onChange(
    values.includes(val) ? values.filter((v) => v !== val) : [...values, val],
  );
  const display = values.length === 0
    ? null
    : values.length === 1
      ? (flat.find((o) => o.value === values[0])?.label ?? values[0])
      : `Several (${values.length})`;
  return (
    <Combobox store={combobox} onOptionSubmit={toggle} withinPortal
      position="bottom-start" width="max-content">
      <Combobox.Target>
        <InputBase {...COMPACT} component="button" type="button" pointer
          aria-label={ariaLabel ?? allLabel}
          onClick={() => combobox.toggleDropdown()}
          rightSectionPointerEvents={values.length > 0 ? 'auto' : 'none'}
          rightSection={values.length > 0
            ? <CloseButton size="xs" aria-label="Clear"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange([]); combobox.closeDropdown(); }} />
            : <Combobox.Chevron size="xs" />}>
          {display
            ? <span style={{ whiteSpace: 'nowrap' }}>{display}</span>
            : <Input.Placeholder>{allLabel}</Input.Placeholder>}
        </InputBase>
      </Combobox.Target>
      {/* The dropdown stays open after each tick (no closeDropdown in
          onOptionSubmit) so options can be ticked in succession. */}
      <Combobox.Dropdown>
        <Combobox.Options>
          {flat.map((o) => (
            <Combobox.Option value={o.value} key={o.value}
              active={values.includes(o.value)}>
              <Group gap="xs" wrap="nowrap">
                <IconCheck size={14}
                  color="var(--mantine-color-blue-6)"
                  style={{ opacity: values.includes(o.value) ? 1 : 0.12 }} />
                <span style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{o.label}</span>
              </Group>
            </Combobox.Option>
          ))}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

/** Inclusive from/to date range, stacked to fit a column header cell.
 *  Either bound may be empty (open-ended range). */
export function ColumnDateRangeFilter({
  from, to, onChange, ariaLabelPrefix = 'Date',
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  ariaLabelPrefix?: string;
}) {
  return (
    <Stack gap={4}>
      <TextInput {...COMPACT} type="date" value={from}
        aria-label={`${ariaLabelPrefix} from`}
        onChange={(e) => onChange(e.currentTarget.value, to)} />
      <TextInput {...COMPACT} type="date" value={to}
        aria-label={`${ariaLabelPrefix} to`}
        onChange={(e) => onChange(from, e.currentTarget.value)} />
    </Stack>
  );
}

/** True when any filter value in the record is non-empty. Drives the
 *  "no matches vs no records" empty-state split and the Clear button. */
export function hasActiveFilters(
  filters: Record<string, string | string[] | null | undefined>,
): boolean {
  return Object.values(filters).some((v) =>
    Array.isArray(v) ? v.length > 0 : v != null && v !== '');
}

/** Column-label wrapper that signals "this column is currently filtered"
 *  (Pedro's polish, 2026-06-12): blue title + small filter glyph + tooltip.
 *  Wrap the label content of every filterable column; pass the column's
 *  own active flag. Blue here is the control-affordance colour (the app's
 *  primary), not the §3j "filed pending approval" badge blue — it sits on
 *  a header label, never on a data row, so the vocabularies don't collide. */
export function FilteredColumnLabel({
  active, children,
}: {
  active: boolean;
  children: React.ReactNode;
}) {
  if (!active) return <>{children}</>;
  // Shape settled with Pedro 2026-06-12 after an orange experiment: soft
  // BLUE background pill, header-default font colour, filter glyph.
  // Rationale: a filter is an ACTIVE CONTROL, and "control engaged" is
  // already blue app-wide (selected status pills, tabs, links, primary
  // buttons). Orange was rejected — it is the §3j "staged, not saved"
  // colour and carries an action obligation a filter doesn't have.
  return (
    <Tooltip label="This column is filtered" withArrow>
      <Group gap={4} wrap="nowrap"
        style={{
          cursor: 'help',
          display: 'inline-flex',
          backgroundColor: 'var(--mantine-color-blue-1)',
          borderRadius: 4,
          padding: '1px 6px',
        }}>
        <span>{children}</span>
        <IconFilter size={12} aria-label="Filtered" />
      </Group>
    </Tooltip>
  );
}
